import { Coffee, CirclePause, UtensilsCrossed, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  useMyAgentStatus,
  useSetAgentStatus,
  AGENT_STATUS_LABELS,
  AGENT_STATUS_COLORS,
  type AgentAvailability,
} from '@/hooks/useAgentStatus';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const STATUS_ICONS: Record<AgentAvailability, React.ReactNode> = {
  available: <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500" />,
  pause: <CirclePause className="w-3 h-3 text-amber-500" />,
  lunch: <UtensilsCrossed className="w-3 h-3 text-orange-500" />,
  busy: <Circle className="w-3 h-3 fill-red-500 text-red-500" />,
  offline: <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />,
};

const SELECTABLE_STATUSES: AgentAvailability[] = ['available', 'pause', 'lunch', 'busy'];

export function AgentStatusSelector({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const currentStatus = useMyAgentStatus();
  const setStatus = useSetAgentStatus();

  const handleStatusChange = (s: AgentAvailability) => {
    setStatus.mutate(s);
    // Persist manual choice to user_preferences
    if (user?.id && user?.company_id) {
      (async () => {
        try {
          const { data: existing } = await (supabase as any)
            .from('user_preferences')
            .select('preferences')
            .eq('user_id', user.id)
            .eq('company_id', user.company_id)
            .maybeSingle();
          const merged = { ...(existing?.preferences || {}), agent_manual_status: s };
          await (supabase as any)
            .from('user_preferences')
            .upsert({ user_id: user.id, company_id: user.company_id, preferences: merged, updated_at: new Date().toISOString() }, { onConflict: 'user_id,company_id' });
        } catch (err) {
          console.error('[AgentStatusSelector] save pref error:', err);
        }
      })();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 border-border/50', compact ? 'h-5 px-2 text-[9px]' : 'h-7 px-2 text-xs')}
        >
          <span className={cn('h-2 w-2 rounded-full shrink-0', AGENT_STATUS_COLORS[currentStatus])} />
          {AGENT_STATUS_LABELS[currentStatus]}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {SELECTABLE_STATUSES.map(s => (
          <DropdownMenuItem
            key={s}
            onClick={() => handleStatusChange(s)}
            className={cn('gap-2 text-xs', currentStatus === s && 'bg-accent')}
          >
            {STATUS_ICONS[s]}
            {AGENT_STATUS_LABELS[s]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
